/**
 * Hauter brand interactions — lightweight, no dependencies
 */
(function () {
  'use strict';

  /* Sticky mobile CTA on product pages */
  function initStickyCta() {
    var bar = document.querySelector('[data-hauter-sticky-cta]');
    if (!bar) return;

    var trigger =
      document.querySelector('[data-hauter-sticky-trigger]') ||
      document.querySelector('.product-form__submit');

    if (!trigger) return;

    var cartDrawer = document.querySelector('cart-drawer');
    var searchDetails = document.querySelector('details-modal.header__search details');
    var triggerInView = true;

    function isOverlayOpen() {
      if (cartDrawer && cartDrawer.classList.contains('active')) return true;
      if (searchDetails && searchDetails.hasAttribute('open')) return true;
      return false;
    }

    function updateBarVisibility() {
      if (isOverlayOpen()) {
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
        return;
      }

      bar.classList.toggle('is-visible', !triggerInView);
      bar.setAttribute('aria-hidden', triggerInView ? 'true' : 'false');
    }

    function syncOverlays() {
      bar.classList.toggle('is-overlay-hidden', isOverlayOpen());
      updateBarVisibility();
    }

    if (cartDrawer) {
      new MutationObserver(syncOverlays).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    if (searchDetails) {
      new MutationObserver(syncOverlays).observe(searchDetails, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    syncOverlays();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          triggerInView = entry.isIntersecting;
          updateBarVisibility();
        });
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );

    observer.observe(trigger);
  }

  /* Smooth scroll for in-page anchors */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#hauter-"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = document.querySelector(link.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* Lazy-load TikTok embeds when section enters viewport */
  function initLazyTikTok() {
    var embeds = document.querySelectorAll('[data-tiktok-id]');
    if (!embeds.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var id = el.dataset.tiktokId;
          if (!id || el.querySelector('iframe')) return;

          var iframe = document.createElement('iframe');
          iframe.src = 'https://www.tiktok.com/embed/v2/' + id;
          iframe.allow = 'encrypted-media';
          iframe.title = 'TikTok video';
          iframe.loading = 'lazy';
          el.appendChild(iframe);
          observer.unobserve(el);
        });
      },
      { rootMargin: '200px' }
    );

    embeds.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* Floating nav clearance — sets --hauter-header-clearance for hero padding-top */
  function initHeaderClearance() {
    var cachedHeroClearance = new WeakMap();
    var resizeTimer = 0;

    function getHeaderBottom() {
      var bottom = 0;
      ['.announcement-bar-section', '.section-header', '.header-wrapper', 'sticky-header'].forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          var rect = el.getBoundingClientRect();
          if (rect.height > 0 && rect.bottom > bottom) bottom = rect.bottom;
        });
      });
      return bottom;
    }

    function measureHeroClearance(hero) {
      var headerBottom = getHeaderBottom();
      var gap = 16;
      var heroTop = hero.getBoundingClientRect().top;

      /* Hero scrolled above viewport — never inflate padding from this reading */
      if (heroTop < -4 || window.scrollY > 8) return null;

      var overlap = headerBottom - heroTop;
      return overlap > 4 ? Math.ceil(overlap + gap) : 0;
    }

    function applyHeroClearance(hero, clearance) {
      if (cachedHeroClearance.get(hero) === clearance) return;
      hero.style.setProperty('--hauter-header-clearance', clearance + 'px');
      cachedHeroClearance.set(hero, clearance);
    }

    function update() {
      /* Mobile Safari fires resize when the URL bar shows/hides mid-scroll — skip */
      if (window.scrollY > 8) return;

      document.querySelectorAll('.hauter-hero').forEach(function (hero) {
        var clearance = measureHeroClearance(hero);
        if (clearance !== null) applyHeroClearance(hero, clearance);
      });

      if (!document.querySelector('.hauter-hero')) {
        document.documentElement.style.setProperty(
          '--hauter-header-clearance',
          Math.ceil(getHeaderBottom() + 16) + 'px'
        );
      }
    }

    function scheduleUpdate() {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        resizeTimer = 0;
        update();
      }, 200);
    }

    update();
    window.addEventListener('load', update);
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    document.addEventListener('shopify:section:load', update);
    document.addEventListener('shopify:section:unload', update);

    if (typeof ResizeObserver !== 'undefined') {
      document.querySelectorAll('.announcement-bar-section, .section-header').forEach(function (el) {
        new ResizeObserver(scheduleUpdate).observe(el);
      });
    }

    setTimeout(update, 50);
    setTimeout(update, 300);
  }

  /* Duplicate marquee items for seamless loop (legacy sections only) */
  function initMarquee() {
    function measureTrack(track) {
      var group = track.querySelector('.hauter-marquee__group');
      if (!group) return;

      var distance = Math.ceil(group.getBoundingClientRect().width);
      if (distance > 0) {
        track.style.setProperty('--hauter-marquee-distance', distance + 'px');
      }
    }

    function setupTrack(track) {
      if (track.dataset.hauterMarqueeReady) return;

      /* Legacy: duplicate items if old markup without groups */
      if (!track.querySelector('.hauter-marquee__group')) {
        track.innerHTML = '<div class="hauter-marquee__group">' + track.innerHTML + '</div>';
        track.innerHTML +=
          '<div class="hauter-marquee__group" aria-hidden="true">' +
          track.querySelector('.hauter-marquee__group').innerHTML +
          '</div>';
      }

      track.dataset.hauterMarqueeReady = 'true';
      measureTrack(track);

      if (typeof ResizeObserver !== 'undefined') {
        var group = track.querySelector('.hauter-marquee__group');
        if (group) {
          new ResizeObserver(function () {
            measureTrack(track);
          }).observe(group);
        }
      }
    }

    document.querySelectorAll('[data-hauter-marquee-track], .hauter-marquee__track').forEach(setupTrack);

    window.addEventListener('load', function () {
      document.querySelectorAll('.hauter-marquee__track').forEach(measureTrack);
    });

    window.addEventListener(
      'resize',
      function () {
        document.querySelectorAll('.hauter-marquee__track').forEach(measureTrack);
      },
      { passive: true }
    );

    document.addEventListener('shopify:section:load', function () {
      document.querySelectorAll('.hauter-marquee__track').forEach(function (track) {
        if (!track.dataset.hauterMarqueeReady) setupTrack(track);
        else measureTrack(track);
      });
    });
  }

  /* Responsive page tables — stack rows on mobile with column labels */
  function initResponsiveTables() {
    var tableSelector =
      'body.page .rte table, .template-page .rte table, .hauter-page-content table, .main-page-content table';

    document.querySelectorAll(tableSelector).forEach(function (table) {
      if (table.dataset.hauterResponsiveReady) return;

      var headers = [];
      table.querySelectorAll('thead th').forEach(function (th) {
        headers.push(th.textContent.trim());
      });

      if (!headers.length) return;

      table.classList.add('hauter-responsive-table');
      table.querySelectorAll('tbody tr').forEach(function (tr) {
        tr.querySelectorAll('td').forEach(function (td, index) {
          if (headers[index]) {
            td.setAttribute('data-label', headers[index]);
          }
        });
      });

      table.dataset.hauterResponsiveReady = 'true';
    });
  }

  document.addEventListener('shopify:section:load', initResponsiveTables);

  /* Mobile hamburger menu — force drawer to the visible viewport */
  function initMobileMenuViewport() {
    var details = document.querySelector('#Details-menu-drawer-container');
    if (!details || details.dataset.hauterViewportReady) return;
    details.dataset.hauterViewportReady = 'true';

    function setHeaderHeight() {
      var header =
        document.querySelector('.section-header') ||
        document.querySelector('sticky-header') ||
        document.querySelector('.header-wrapper');
      var height = 70;

      if (header) {
        height = Math.round(header.getBoundingClientRect().height || height);
      }

      document.documentElement.style.setProperty(
        '--hauter-menu-header-height',
        Math.min(Math.max(height, 56), 90) + 'px'
      );
    }

    function syncMenuState() {
      var isOpen = details.hasAttribute('open');
      document.documentElement.classList.toggle('hauter-mobile-menu-open', isOpen);
      document.body.classList.toggle('hauter-mobile-menu-open', isOpen);

      if (isOpen) {
        setHeaderHeight();
      }
    }

    details.addEventListener('toggle', syncMenuState);
    details.addEventListener('click', function () {
      setTimeout(syncMenuState, 0);
      setTimeout(syncMenuState, 120);
    });

    new MutationObserver(syncMenuState).observe(details, {
      attributes: true,
      attributeFilter: ['open', 'class'],
    });

    window.addEventListener('resize', function () {
      if (details.hasAttribute('open')) setHeaderHeight();
    });

    syncMenuState();
  }

  /* FAQ search + category filter */
  function initFaq() {
    document.querySelectorAll('[data-hauter-faq]').forEach(function (section) {
      var search = section.querySelector('[data-hauter-faq-search]');
      var nav = section.querySelector('[data-hauter-faq-nav]');
      var empty = section.querySelector('[data-hauter-faq-empty]');
      var categories = section.querySelectorAll('[data-hauter-faq-category]');
      var items = section.querySelectorAll('[data-hauter-faq-item]');
      var activeFilter = 'all';

      function applyFilters() {
        var query = search ? search.value.trim().toLowerCase() : '';
        var visibleCount = 0;

        categories.forEach(function (category) {
          var categoryId = category.getAttribute('data-hauter-faq-category');
          var categoryHidden = activeFilter !== 'all' && activeFilter !== categoryId;
          var categoryVisibleItems = 0;

          category.querySelectorAll('[data-hauter-faq-item]').forEach(function (item) {
            var text = item.textContent.toLowerCase();
            var matchesQuery = !query || text.indexOf(query) !== -1;
            var matchesFilter = !categoryHidden;
            var show = matchesQuery && matchesFilter;

            item.classList.toggle('is-hidden', !show);
            if (show) {
              categoryVisibleItems += 1;
              visibleCount += 1;
            }
          });

          category.classList.toggle('is-hidden', categoryHidden || categoryVisibleItems === 0);
        });

        if (empty) {
          empty.hidden = visibleCount > 0;
        }
      }

      if (search) {
        search.addEventListener('input', applyFilters);
      }

      if (nav) {
        nav.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-hauter-faq-filter]');
          if (!btn) return;

          activeFilter = btn.getAttribute('data-hauter-faq-filter');
          nav.querySelectorAll('.hauter-faq__nav-btn').forEach(function (el) {
            el.classList.toggle('is-active', el === btn);
          });
          applyFilters();
        });
      }

      applyFilters();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initHeaderClearance();
    initStickyCta();
    initSmoothScroll();
    initLazyTikTok();
    initMarquee();
    initResponsiveTables();
    initMobileMenuViewport();
    initFaq();
  });
})();
